/* The ERP, wired together.
 *
 * Four routes carry the whole product, because the engine does the work:
 * a list, a form, browse-everything, and a home. Adding "Purchase Orders" to
 * the menu is one line in nav.ts, not a screen.
 */
import { useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  AppShell, CommandPalette, Toaster, endSession, getProfile, isSignedIn,
  rememberReturnTo, signOut, useCommandKey,
} from '@jackdesk/shell'
import type { Command, NavGroup } from '@jackdesk/shell'
import {
  BomView, ErpClient, ErpProvider, FormView, ImportView, MakeWork, PacksSettings, ReportView,
  WorkHome, WorkItem, WorkProject, WorkProjects, WorkReports, homeLabel, useErp, usePacksActive,
  DocFlow, FLOW_DOCTYPES,
} from '@jackdesk/erp-kit'
import type { FoundDoc, ModuleState } from '@jackdesk/erp-kit'
import { API_BASE, AUTH_BASE, headers, readDevIdentity, writeDevIdentity } from './config'
import { SignIn } from './SignIn'
import { MENU, labelFor, routeFor, isSingle, openRouteFor } from './nav'
import type { IndexEntry } from './nav'
import { Browse } from './Browse'
import { Reports } from './Reports'
import { Dashboard } from './Dashboard'
import { Customise } from './Customise'
import { Automation } from './Automation'
import { Contacts } from './Contacts'
import { Gst } from './Gst'
import { Website } from './Website'
import { Connection } from './Connection'
import { ModulesContext, readableModules } from './modules'
import { Entitled } from './Entitled'
import { Notices } from './Notices'
import { People } from './People'
import { ListScreen } from './ListScreen'
import { Home } from './Home'
import index from './doctypes.generated.json'

const ALL = index as IndexEntry[]

export function App() {
  const client = useMemo(() => new ErpClient({
    base: API_BASE, headers,
    // An expired or revoked token means the session is over, not that every
    // screen is forbidden. Development keeps its identity switcher: there is
    // no session there to end, and bouncing to /signin would make the local
    // gateway unusable.
    onUnauthorized: import.meta.env.DEV ? undefined : endSession,
  }), [])

  // In development the identity switcher stands in for a session, so the
  // journeys and the local gateway keep working without anybody signing in.
  // A production build has no such door: `headers()` sends nothing without a
  // token, so every call would 401 anyway — better to say so on a sign-in
  // page than to draw a whole shell full of empty screens.
  const needsSignIn = !import.meta.env.DEV && !isSignedIn()
  if (needsSignIn && window.location.pathname !== '/signin') {
    // Remember where they were heading, so the hand-off lands there and not
    // on the home page.
    rememberReturnTo()
    window.location.replace('/signin')
    return null
  }
  if (needsSignIn) return <SignIn />

  return (
    <ErpProvider client={client}>
      <Toaster>
        <Chrome />
      </Toaster>
    </ErpProvider>
  )
}

function Chrome() {
  const navigate = useNavigate()
  const erp = useErp()
  const [palette, setPalette] = useState(false)
  // What this organisation has. Asked once; the menu and the screens share it.
  const [modules, setModules] = useState<ModuleState | null>(null)
  useEffect(() => { erp.modules().then(setModules).catch(() => setModules(null)) }, [erp])
  const readable = useMemo(() => readableModules(modules), [modules])
  // The packs switched on here: their work gets its own place in the menu.
  const { active: packs } = usePacksActive()
  useCommandKey(() => setPalette(true))

  const nav: NavGroup[] = useMemo(() => [
    ...(packs?.packs.length ? [{ title: 'Work', items: [
      // The pack's own word: "My jobs" (owner, 2026-10-08: "My Tasks" confused people).
      { label: homeLabel(packs), to: '/work' },
      { label: 'Projects', to: '/work/projects' },
    ] }] : []),
    { title: 'Overview', items: [
      { label: 'Home', to: '/' },
      { label: 'Dashboard', to: '/dashboard' },
      { label: 'Browse everything', to: '/browse' },
      { label: 'Reports', to: '/reports' },
    ] },
    ...MENU.map((s) => ({
      title: s.title,
      // Only what this organisation has. A lapsed area stays in the menu --
      // it is still readable -- and only one never bought is left out.
      items: s.entries
        .filter((e) => !e.doctype || !readable
                       || readable.has(index.find((d) => d.n === e.doctype)?.m ?? ''))
        .map((e) => ({ label: e.label, to: routeFor(e) })),
    })),
  ], [readable, packs])

  const commands: Command[] = useMemo(() => {
    const fromMenu = MENU.flatMap((s) => s.entries.map((e) => ({
      id: `menu:${e.doctype ?? e.report ?? 'all-reports'}`,
      label: e.label,
      // The DocType name is searchable too: somebody who knows ERPNext types
      // "sales invoice", somebody who does not types "invoice", and both
      // should land on the same row.
      hint: (e.doctype ?? e.report) !== e.label ? (e.doctype ?? e.report) : undefined,
      group: s.title,
      boost: 2,
      run: () => navigate(routeFor(e)),
    })))
    const menuTypes = new Set(MENU.flatMap((s) => s.entries.map((e) => e.doctype)))
    const rest = ALL.filter((e) => !menuTypes.has(e.n)).map((e) => ({
      id: `all:${e.n}`,
      label: e.n,
      group: e.m,
      run: () => navigate(openRouteFor(e.n)),
    }))
    return [...fromMenu, ...rest]
  }, [navigate])

  const path = window.location.pathname

  return (
    <>
      <ModulesContext.Provider value={modules}>
      <AppShell
        product="Jackdesk ERP"
        nav={nav}
        current={path}
        onNavigate={(to) => navigate(to)}
        onSearch={() => setPalette(true)}
        who={<WhoAmI />}
        actions={<Notices />}
      >
        <Connection />
        <Routes>
          <Route path="/" element={<Home onOpen={(dt) => navigate(openRouteFor(dt))} onGo={navigate} />} />
          <Route path="/browse" element={<Browse onOpen={(dt) => navigate(openRouteFor(dt))} />} />
          <Route path="/dashboard" element={<Dashboard onOpen={(dt) => navigate(openRouteFor(dt))} onGo={navigate} />} />
          <Route path="/reports" element={<Reports onOpen={(r) => navigate(`/report/${encodeURIComponent(r)}`)} />} />
          <Route path="/report/:name" element={<ReportScreen />} />
          <Route path="/list/:doctype" element={<ListRoute />} />
          <Route path="/import/:doctype" element={<ImportRoute />} />
          <Route path="/people" element={<People />} />
          <Route path="/work" element={<WorkHomeRoute />} />
          <Route path="/work/reports" element={<WorkReportsRoute />} />
          <Route path="/work/reports/:key" element={<WorkReportsRoute />} />
          <Route path="/work/new/:pack/:kind" element={<MakeWorkRoute />} />
          <Route path="/work/new/:pack/:kind/for/:project" element={<MakeWorkRoute />} />
          <Route path="/work/projects" element={<WorkProjectsRoute />} />
          <Route path="/work/projects/:project" element={<WorkProjectRoute />} />
          <Route path="/work/:name" element={<WorkItemRoute />} />
          <Route path="/packs" element={<PacksSettings onOpenWork={() => navigate('/work')} />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/gst" element={<Gst />} />
          <Route path="/website" element={
            <Website
              onOpen={(dt, name) => navigate(`/doc/${encodeURIComponent(dt)}/${encodeURIComponent(name)}`)}
              onOpenList={(dt) => navigate(openRouteFor(dt))} />} />
          <Route path="/automation" element={<Automation />} />
          <Route path="/automation/:doctype" element={<Automation />} />
          <Route path="/customise" element={<Customise />} />
          <Route path="/customise/:doctype" element={<Customise />} />
          <Route path="/doc/:doctype/:name" element={<FormScreen />} />
          <Route path="/money/:doctype/:name" element={<MoneyRoute />} />
          {/* Already signed in and asking for the sign-in page: go home
              rather than offering a second sign-in. */}
          <Route path="/signin" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
      </ModulesContext.Provider>
      {palette ? (
        <CommandPalette
          commands={commands}
          onClose={() => setPalette(false)}
          // The other half of a search box: real documents, not just screens.
          lookUp={async (q) => {
            const { hits } = await erp.find(q)
            return hits.map((h: FoundDoc) => ({
              id: `doc:${h.doctype}:${h.name}`,
              label: h.title || h.name,
              hint: h.title ? `${h.what} · ${h.name}` : h.what,
              group: 'In your records',
              run: () => navigate(
                `/doc/${encodeURIComponent(h.doctype)}/${encodeURIComponent(h.name)}`),
            }))
          }}
        />
      ) : null}
    </>
  )
}

function WorkHomeRoute() {
  const navigate = useNavigate()
  const erp = useErp()
  // Only an owner is offered the way to switch a pack on.
  const [mayManage, setMayManage] = useState(false)
  useEffect(() => {
    erp.packs().then((r) => setMayManage(r.may_manage)).catch(() => setMayManage(false))
  }, [erp])
  return (
    <WorkHome
      mayManage={mayManage}
      onOpen={(name) => navigate(`/work/${encodeURIComponent(name)}`)}
      onMake={(pack, kind) => navigate(`/work/new/${encodeURIComponent(pack)}/${encodeURIComponent(kind)}`)}
      onSetUp={() => navigate('/packs')}
      onReports={() => navigate('/work/reports')}
      onProjects={() => navigate('/work/projects')}
    />
  )
}

function WorkProjectsRoute() {
  const navigate = useNavigate()
  return <WorkProjects onOpen={(p) => navigate(`/work/projects/${encodeURIComponent(p)}`)}
                       onJobs={() => navigate('/work')} />
}

function WorkProjectRoute() {
  const { project = '' } = useParams()
  const navigate = useNavigate()
  return (
    <WorkProject key={project} name={decodeURIComponent(project)}
                 onOpen={(n) => navigate(`/work/${encodeURIComponent(n)}`)}
                 onBack={() => navigate('/work/projects')}
                 onMake={(pack, kind, p) => navigate(
                   `/work/new/${encodeURIComponent(pack)}/${encodeURIComponent(kind)}/for/${encodeURIComponent(p)}`)} />
  )
}

function WorkReportsRoute() {
  const { key } = useParams()
  const navigate = useNavigate()
  return (
    <WorkReports reportKey={key ? decodeURIComponent(key) : undefined}
                 onPick={(k) => navigate(`/work/reports/${encodeURIComponent(k)}`)}
                 onOpenTask={(n) => navigate(`/work/${encodeURIComponent(n)}`)}
                 onBack={() => navigate('/work')} />
  )
}

function MakeWorkRoute() {
  const { pack = '', kind = '', project } = useParams()
  const navigate = useNavigate()
  return (
    <MakeWork key={`${pack}/${kind}/${project ?? ''}`} packId={decodeURIComponent(pack)} kindKey={decodeURIComponent(kind)}
              project={project ? decodeURIComponent(project) : undefined}
              onDone={(name) => navigate(`/work/${encodeURIComponent(name)}`, { replace: true })}
              onCancel={() => navigate('/work')} />
  )
}

/** A sale or a purchase opens where it is and what comes next (PK-65); anything else as its form. */
const docPath = (doctype: string, name: string) =>
  `/${FLOW_DOCTYPES.has(doctype) ? 'money' : 'doc'}/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}`

function MoneyRoute() {
  const { doctype = '', name = '' } = useParams()
  const navigate = useNavigate()
  const dt = decodeURIComponent(doctype)
  const nm = decodeURIComponent(name)
  return <DocFlow key={`${dt}/${nm}`} doctype={dt} name={nm}
                  booksUrl="https://store.jackdesk.com/#settings"
                  onOpen={(d, n) => navigate(docPath(d, n))}
                  onEdit={(d, n) => navigate(`/doc/${encodeURIComponent(d)}/${encodeURIComponent(n)}`)} />
}

function WorkItemRoute() {
  const { name = '' } = useParams()
  const navigate = useNavigate()
  return (
    <WorkItem key={name} name={decodeURIComponent(name)}
              onOpen={(n) => navigate(`/work/${encodeURIComponent(n)}`)}
              onOpenInvoice={(n) => navigate(docPath('Sales Invoice', n))}
              onBack={() => navigate('/work')} />
  )
}

function ReportScreen() {
  const { name = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  // Filters can arrive from a drill-through, so the report opens already
  // narrowed to what the person clicked.
  const incoming = (location.state as { filters?: Record<string, unknown> } | null)?.filters
  return (
    <ReportView
      key={`${name}:${incoming ? JSON.stringify(incoming) : ''}`}
      name={decodeURIComponent(name)}
      initialFilters={incoming}
      onOpenDoc={(doctype, docname) =>
        navigate(`/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(docname)}`)}
      onOpenReport={(report, filters) =>
        navigate(`/report/${encodeURIComponent(report)}`, { state: { filters } })}
    />
  )
}

function ImportRoute() {
  const { doctype = '' } = useParams()
  const navigate = useNavigate()
  const dt = decodeURIComponent(doctype)
  return <ImportView doctype={dt} title={labelFor(dt)}
                     onDone={() => navigate(`/list/${encodeURIComponent(dt)}`)} />
}

function ListRoute() {
  const { doctype = '' } = useParams()
  const navigate = useNavigate()
  const dt = decodeURIComponent(doctype)
  // A Single has one record named after the DocType and no table to list, so
  // asking for its list is a 500 from Frappe, not an empty page. Menu and
  // palette already route around it; this catches a typed or bookmarked URL.
  if (isSingle(dt)) {
    return <Navigate to={`/doc/${encodeURIComponent(dt)}/${encodeURIComponent(dt)}`} replace />
  }
  return (
    <Entitled doctype={decodeURIComponent(doctype)}
              onSeeStore={() => navigate('/browse')}>
      {(readOnly) => (
    <ListScreen
      key={doctype}
      readOnly={readOnly}
      doctype={decodeURIComponent(doctype)}
      onOpen={(name) => navigate(docPath(decodeURIComponent(doctype), name))}
      onNew={() => navigate(`/doc/${encodeURIComponent(doctype)}/new`)}
      onImport={() => navigate(`/import/${encodeURIComponent(doctype)}`)}
    />
      )}
    </Entitled>
  )
}

function FormScreen() {
  const { doctype = '', name = '' } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dt = decodeURIComponent(doctype)
  // Duplicate hands the copied values through the router, so the new form is
  // a real navigation the Back button understands.
  const initial = (location.state as { initial?: Record<string, unknown> } | null)?.initial
  return (
    <FormView
      key={`${dt}/${name}/${initial ? 'copy' : ''}`}
      initial={initial}
      onDuplicate={(d, values) =>
        navigate(`/doc/${encodeURIComponent(d)}/new`, { state: { initial: values } })}
      onDeleted={(d) => navigate(`/list/${encodeURIComponent(d)}`, { replace: true })}
      doctype={dt}
      name={decodeURIComponent(name)}
      // A bill of materials is a tree, a cost and a "what else uses this",
      // and none of those are fields. Everything else on the form still
      // draws itself from the metadata.
      below={dt === 'BOM' ? (doc) => (
        <BomView name={String(doc.name)} item={String(doc.item ?? '')}
                 onOpenBom={(b) => navigate(`/doc/BOM/${encodeURIComponent(b)}`)}
                 onOpenItem={(i) => navigate(`/doc/Item/${encodeURIComponent(i)}`)} />
      ) : undefined}
      onSaved={(saved) => {
        // A new document becomes its own URL; so does an amendment, which
        // is a *different* document and must not silently replace the one
        // the person was looking at.
        if (!saved.name) return
        const to = `/doc/${encodeURIComponent(dt)}/${encodeURIComponent(String(saved.name))}`
        if (name === 'new') navigate(to, { replace: true })
        else if (String(saved.name) !== decodeURIComponent(name)) navigate(to)
      }}
      header={isSingle(dt) ? undefined : (
        <button className="btn btn-quiet btn-sm" style={{ justifySelf: 'start' }}
                onClick={() => navigate(`/list/${encodeURIComponent(dt)}`)}>
          ← {labelFor(dt)}
        </button>
      )}
    />
  )
}

/** Development only: the identity switcher the local gateway believes. In a
 *  production build `headers()` returns nothing and this shows who is signed
 *  in instead. */
function WhoAmI() {
  const [who, setWho] = useState(readDevIdentity())
  const profile = getProfile()
  // A real session is who they are, whatever the development switcher says.
  if (profile) {
    return (
      <div className="row" style={{ gap: 6, minWidth: 0 }}>
        <span className="small muted truncate" title={profile.user.email}>
          {profile.user.email ?? 'Signed in'}
        </span>
        <button className="btn btn-quiet btn-sm" onClick={() => void signOut(AUTH_BASE)}>
          Sign out
        </button>
      </div>
    )
  }
  if (!import.meta.env.DEV) return <span className="small muted truncate">{who.user}</span>
  const roles = ['owner', 'manager', 'payroll', 'auditor', 'employee']
  return (
    <select
      className="select" style={{ maxWidth: 130, minHeight: 28, fontSize: 12 }}
      aria-label="Development identity" value={who.role}
      onChange={(e) => {
        const next = { ...who, role: e.target.value, user: `${e.target.value}@demo.test` }
        setWho(next); writeDevIdentity(next); window.location.reload()
      }}
    >
      {roles.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
  )
}
